import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Key, Loader2, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface Usuario {
  id: string;
  email: string;
  nome: string;
}

interface RedefinirSenhaDialogProps {
  usuario: Usuario;
  onSenhaRedefinida?: () => void;
}

export function RedefinirSenhaDialog({ usuario, onSenhaRedefinida }: RedefinirSenhaDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { canManageUsers } = useAuth();
  
  const [formData, setFormData] = useState({
    novaSenha: "",
    confirmarSenha: ""
  });

  // Verificar se o usuário atual pode redefinir senhas
  const canRedefinirSenha = canManageUsers();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validar se as senhas coincidem
    if (formData.novaSenha !== formData.confirmarSenha) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    // Validar se a senha tem pelo menos 6 caracteres
    if (formData.novaSenha.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      // Chamar a Edge Function para redefinir a senha
      const { data, error } = await supabase.functions.invoke('reset-password', {
        body: {
          targetUserId: usuario.id,
          newPassword: formData.novaSenha
        }
      });

      if (error) {
        console.error('Erro na Edge Function:', error);
        setError('Erro ao redefinir senha: ' + error.message);
        return;
      }

      if (data?.error) {
        setError(data.error);
        return;
      }

      if (data?.success) {
        setSuccess(true);
        toast.success('Senha redefinida com sucesso!');
        
        // Fechar o modal após um delay
        setTimeout(() => {
          setOpen(false);
          setFormData({ novaSenha: "", confirmarSenha: "" });
          setShowPassword(false);
          setSuccess(false);
          
          if (onSenhaRedefinida) {
            onSenhaRedefinida();
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      setError('Erro inesperado ao redefinir senha. Entre em contato com o administrador do sistema.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({ novaSenha: "", confirmarSenha: "" });
    setShowPassword(false);
    setError(null);
    setSuccess(false);
  };

  if (!canRedefinirSenha) {
    return null; // Não mostrar o botão se não tiver permissão
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Key className="h-4 w-4" />
          Redefinir Senha
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Redefinir Senha</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-sm text-gray-600">
            Redefinindo senha para: <strong>{usuario.nome}</strong> ({usuario.email})
          </div>
          
          {success && (
            <div className="flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-700">
                <p className="font-medium">Sucesso!</p>
                <p>Senha redefinida com sucesso. O modal será fechado automaticamente.</p>
              </div>
            </div>
          )}
          
          {error && (
            <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-700">
                <p className="font-medium">Erro:</p>
                <p className="whitespace-pre-line">{error}</p>
              </div>
            </div>
          )}
          
          <div>
            <Label htmlFor="novaSenha">Nova Senha</Label>
            <div className="relative">
              <Input
                id="novaSenha"
                type={showPassword ? "text" : "password"}
                value={formData.novaSenha}
                onChange={(e) => setFormData({ ...formData, novaSenha: e.target.value })}
                placeholder="Digite a nova senha"
                required
                minLength={6}
                disabled={loading || success}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading || success}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Mínimo de 6 caracteres
            </p>
          </div>
          
          <div>
            <Label htmlFor="confirmarSenha">Confirmar Nova Senha</Label>
            <div className="relative">
              <Input
                id="confirmarSenha"
                type={showPassword ? "text" : "password"}
                value={formData.confirmarSenha}
                onChange={(e) => setFormData({ ...formData, confirmarSenha: e.target.value })}
                placeholder="Confirme a nova senha"
                required
                minLength={6}
                disabled={loading || success}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={loading || success}
            >
              Limpar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading || success}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || success}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redefinindo...
                </>
              ) : (
                "Redefinir Senha"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 